import {
    CompetitionGame,
    Game
} from "./mexfbaApi";

export interface GamePrediction {
    localProbability: number;
    visitorProbability: number;

    localRecentGames: number;
    visitorRecentGames: number;

    localHistoricalGames: number;
    visitorHistoricalGames: number;

    localElo: number;
    visitorElo: number;

    model: "forma-y-elo" | "elo";
}

interface RecentResult {
    day: string;
    gameId: string;
    margin: number;
}

interface TeamState {
    elo: number;
    historicalGames: number;
    currentCompetitionResults: RecentResult[];
}

interface HistoricalGame {
    id: string;
    day: string;

    localId: string;
    visitorId: string;

    competitionId: string;

    localScore: number;
    visitorScore: number;
}

/*
    Parámetros iniciales de prueba.

    No están calibrados con tus partidos. Es importante evaluarlos
    contra resultados históricos antes de considerarlos definitivos.
*/
const INITIAL_ELO = 1200;
const ELO_SCALE = 600;
const ELO_K = 20;

const MAX_RECENT_GAMES = 5;
const SCORE_CAP = 35;

const FORM_PRIOR_GAMES = 3;
const FORM_SCALE = 14;

const MIN_HISTORICAL_GAMES = 2;
const MIN_CURRENT_GAMES_FOR_FORM = 3;

const FORM_WEIGHT = 0.8;
const ELO_WEIGHT = 0.2;

const MEXICO_TIME_ZONE = "America/Monterrey";

function clamp(
    value: number,
    minimum: number,
    maximum: number
): number {
    return Math.max(
        minimum,
        Math.min(maximum, value)
    );
}

/*
    Aceptamos fechas que empiezan por YYYY-MM-DD.

    Aunque algunos registros puedan contener hora en `fecha`,
    para el corte histórico utilizamos el día completo para
    no incluir accidentalmente partidos del mismo día.
*/
function getCalendarDay(
    value?: string
): string | null {
    if (!value) {
        return null;
    }

    const match = value.match(
        /^(\d{4})-(\d{2})-(\d{2})(?:$|[T ])/
    );

    if (!match) {
        return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);

    const date = new Date(
        Date.UTC(year, month - 1, day)
    );

    const valid =
        date.getUTCFullYear() === year &&
        date.getUTCMonth() === month - 1 &&
        date.getUTCDate() === day;

    if (!valid) {
        return null;
    }

    return `${match[1]}-${match[2]}-${match[3]}`;
}

function getClockTime(
    value?: string
): string | null {
    if (!value) {
        return null;
    }

    /*
        Soporta H:mm, HH:mm y HH:mm:ss.

        Si tu API usa formatos como "7:00 PM", habría que
        agregar ese formato explícitamente.
    */
    const match = value.trim().match(
        /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/
    );

    if (!match) {
        return null;
    }

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = Number(match[3] || "0");

    if (
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59 ||
        seconds < 0 ||
        seconds > 59
    ) {
        return null;
    }

    return (
        `${String(hours).padStart(2, "0")}:` +
        `${String(minutes).padStart(2, "0")}:` +
        `${String(seconds).padStart(2, "0")}`
    );
}

interface MonterreyTime {
    day: string;
    time: string;
}

/*
    Convertimos el instante actual a las partes de fecha y hora
    de Monterrey. Así no dependemos de la zona del dispositivo.
*/
function getMonterreyTime(
    now: Date
): MonterreyTime {
    const formatter = new Intl.DateTimeFormat(
        "en-CA",
        {
            timeZone: MEXICO_TIME_ZONE,

            year: "numeric",
            month: "2-digit",
            day: "2-digit",

            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",

            hourCycle: "h23"
        }
    );

    const parts = formatter.formatToParts(now);

    function part(
        type: Intl.DateTimeFormatPartTypes
    ): string {
        const found = parts.find(function (item) {
            return item.type === type;
        });

        return found?.value || "";
    }

    return {
        day:
            `${part("year")}-` +
            `${part("month")}-` +
            `${part("day")}`,

        time:
            `${part("hour")}:` +
            `${part("minute")}:` +
            `${part("second")}`
    };
}

function getValidScore(
    value: string | number | null | undefined
): number | null {
    if (
        value === undefined ||
        value === null ||
        (
            typeof value === "string" &&
            value.trim() === ""
        )
    ) {
        return null;
    }

    const score = Number(value);

    if (
        !Number.isInteger(score) ||
        score < 0
    ) {
        return null;
    }

    return score;
}

/*
    El historial tiene resultados administrativos o pendientes.

    Reglas conservadoras:

    - Se excluye 0–0 porque aparece en partidos programados.
    - Se excluyen 1–0 y 0–1 porque pueden ser forfait.
    - Se excluye una sede que indique explícitamente forfait.

    Estas reglas no sustituyen un campo oficial de estado:
    podrían excluir algún resultado real y no identifican todos
    los resultados administrativos.
*/
function isUsableHistoricalResult(
    game: Game,
    localScore: number,
    visitorScore: number
): boolean {
    if (
        localScore === 0 &&
        visitorScore === 0
    ) {
        return false;
    }

    const isOneZero =
        (localScore === 1 && visitorScore === 0) ||
        (localScore === 0 && visitorScore === 1);

    if (isOneZero) {
        return false;
    }

    if (/forfait/i.test(game.sede || "")) {
        return false;
    }

    return true;
}

function createTeamState(): TeamState {
    return {
        elo: INITIAL_ELO,
        historicalGames: 0,
        currentCompetitionResults: []
    };
}

function getOrCreateTeam(
    states: Map<string, TeamState>,
    teamId: string
): TeamState {
    const existing = states.get(teamId);

    if (existing) {
        return existing;
    }

    const created = createTeamState();

    states.set(teamId, created);

    return created;
}

function getExpectedResult(
    localElo: number,
    visitorElo: number
): number {
    return 1 / (
        1 +
        Math.pow(
            10,
            (visitorElo - localElo) / ELO_SCALE
        )
    );
}

function getActualResult(
    localScore: number,
    visitorScore: number
): number {
    if (localScore > visitorScore) {
        return 1;
    }

    if (localScore < visitorScore) {
        return 0;
    }

    return 0.5;
}

function getSmoothedForm(
    results: RecentResult[]
): number {
    const recent = results
        .slice()
        .sort(function (first, second) {
            return (
                first.day.localeCompare(second.day) ||
                first.gameId.localeCompare(second.gameId)
            );
        })
        .slice(-MAX_RECENT_GAMES);

    const total = recent.reduce(
        function (sum, result) {
            return sum + result.margin;
        },
        0
    );

    return total / (
        recent.length + FORM_PRIOR_GAMES
    );
}

function getFormProbability(
    localResults: RecentResult[],
    visitorResults: RecentResult[]
): number {
    const difference =
        getSmoothedForm(localResults) -
        getSmoothedForm(visitorResults);

    return 1 / (
        1 + Math.exp(-difference / FORM_SCALE)
    );
}

/*
    Reglas de visualización:

    - Sin fecha: no se muestra.
    - Fecha futura: se muestra, incluso si aún falta la hora.
    - Hoy con hora válida: se muestra hasta la hora programada.
    - Hoy sin hora válida: se oculta por seguridad.
    - Después del inicio: se oculta.

    Se usa la hora de Monterrey independientemente de la zona
    configurada en el dispositivo.
*/
export function canShowPregamePrediction(
    game: CompetitionGame,
    now: Date = new Date()
): boolean {
    const gameDay = getCalendarDay(game.fecha);

    if (!gameDay) {
        return false;
    }

    const current = getMonterreyTime(now);

    if (gameDay > current.day) {
        return true;
    }

    if (gameDay < current.day) {
        return false;
    }

    const gameTime = getClockTime(game.hora);

    if (!gameTime) {
        return false;
    }

    return current.time < gameTime;
}

function getHistoricalGames(
    targetGame: CompetitionGame,
    games: Game[],
    cutoffDay: string
): HistoricalGame[] {
    const unique = new Map<string, HistoricalGame>();

    games.forEach(function (game) {
        const gameId = String(game.id || "").trim();

        if (
            !gameId ||
            unique.has(gameId) ||
            gameId === String(targetGame.id)
        ) {
            return;
        }

        const day = getCalendarDay(game.fecha);

        const localId = String(
            game.id_local || ""
        ).trim();

        const visitorId = String(
            game.id_visita || ""
        ).trim();

        const competitionId = String(
            game.id_comp || ""
        ).trim();

        const localScore = getValidScore(
            game.resLocal
        );

        const visitorScore = getValidScore(
            game.resVisita
        );

        if (
            !day ||
            day >= cutoffDay ||
            !localId ||
            !visitorId ||
            localId === visitorId ||
            !competitionId ||
            localScore === null ||
            visitorScore === null
        ) {
            return;
        }

        if (
            !isUsableHistoricalResult(
                game,
                localScore,
                visitorScore
            )
        ) {
            return;
        }

        unique.set(gameId, {
            id: gameId,
            day,

            localId,
            visitorId,

            competitionId,

            localScore,
            visitorScore
        });
    });

    return Array.from(unique.values()).sort(
        function (first, second) {
            return (
                first.day.localeCompare(second.day) ||
                first.id.localeCompare(second.id)
            );
        }
    );
}

export function createGamePrediction(
    targetGame: CompetitionGame,
    games: Game[],
    now: Date = new Date()
): GamePrediction | null {
    const targetDay = getCalendarDay(
        targetGame.fecha
    );

    const localId = String(
        targetGame.id_local || ""
    ).trim();

    const visitorId = String(
        targetGame.id_visita || ""
    ).trim();

    const competitionId = String(
        targetGame.id_comp || ""
    ).trim();

    if (
        !targetDay ||
        !localId ||
        !visitorId ||
        localId === visitorId ||
        !competitionId
    ) {
        return null;
    }

    const currentDay = getMonterreyTime(now).day;

    /*
        Para un partido futuro, no usamos resultados de hoy,
        aunque el JSON ya contenga un marcador.

        Para una prueba histórica, no usamos resultados del
        mismo día que el partido que se quiere pronosticar.
    */
    const cutoffDay =
        targetDay < currentDay
            ? targetDay
            : currentDay;

    const history = getHistoricalGames(
        targetGame,
        games,
        cutoffDay
    );

    const states = new Map<string, TeamState>();

    /*
        Los cambios Elo de todos los partidos de un mismo día
        se calculan a partir de las calificaciones existentes
        al inicio de ese día.

        Esto evita inventar un orden por hora cuando la mayoría
        de los registros históricos no la tiene.
    */
    let index = 0;

    while (index < history.length) {
        const day = history[index].day;

        const dailyGames: HistoricalGame[] = [];

        while (
            index < history.length &&
            history[index].day === day
        ) {
            dailyGames.push(history[index]);
            index += 1;
        }

        const dailyChanges = new Map<string, number>();

        dailyGames.forEach(function (game) {
            const local = getOrCreateTeam(
                states,
                game.localId
            );

            const visitor = getOrCreateTeam(
                states,
                game.visitorId
            );

            const expected = getExpectedResult(
                local.elo,
                visitor.elo
            );

            const actual = getActualResult(
                game.localScore,
                game.visitorScore
            );

            const change =
                ELO_K * (actual - expected);

            dailyChanges.set(
                game.localId,
                (dailyChanges.get(game.localId) || 0) +
                    change
            );

            dailyChanges.set(
                game.visitorId,
                (dailyChanges.get(game.visitorId) || 0) -
                    change
            );

            local.historicalGames += 1;
            visitor.historicalGames += 1;

            /*
                La forma reciente sólo usa partidos de la
                competencia del partido pronosticado.

                Elo sí utiliza otras competencias anteriores.
            */
            if (
                game.competitionId === competitionId
            ) {
                const margin = clamp(
                    game.localScore - game.visitorScore,
                    -SCORE_CAP,
                    SCORE_CAP
                );

                local.currentCompetitionResults.push({
                    day: game.day,
                    gameId: game.id,
                    margin
                });

                visitor.currentCompetitionResults.push({
                    day: game.day,
                    gameId: game.id,
                    margin: -margin
                });
            }
        });

        dailyChanges.forEach(
            function (change, teamId) {
                const team = getOrCreateTeam(
                    states,
                    teamId
                );

                team.elo += change;
            }
        );
    }

    const local = states.get(localId);
    const visitor = states.get(visitorId);

    if (
        !local ||
        !visitor ||
        local.historicalGames < MIN_HISTORICAL_GAMES ||
        visitor.historicalGames < MIN_HISTORICAL_GAMES
    ) {
        return null;
    }

    const localRecentGames = Math.min(
        local.currentCompetitionResults.length,
        MAX_RECENT_GAMES
    );

    const visitorRecentGames = Math.min(
        visitor.currentCompetitionResults.length,
        MAX_RECENT_GAMES
    );

    const eloProbability = getExpectedResult(
        local.elo,
        visitor.elo
    );

    const hasEnoughCurrentForm =
        localRecentGames >= MIN_CURRENT_GAMES_FOR_FORM &&
        visitorRecentGames >= MIN_CURRENT_GAMES_FOR_FORM;

    let localProbability = eloProbability;

    let model: GamePrediction["model"] = "elo";

    if (hasEnoughCurrentForm) {
        const formProbability = getFormProbability(
            local.currentCompetitionResults,
            visitor.currentCompetitionResults
        );

        localProbability =
            FORM_WEIGHT * formProbability +
            ELO_WEIGHT * eloProbability;

        model = "forma-y-elo";
    }

    return {
        localProbability,
        visitorProbability: 1 - localProbability,

        localRecentGames,
        visitorRecentGames,

        localHistoricalGames: local.historicalGames,
        visitorHistoricalGames: visitor.historicalGames,

        localElo: local.elo,
        visitorElo: visitor.elo,

        model
    };
}
